import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Upload, FileText, CheckCircle, AlertCircle, RefreshCw,
  Package, Layers, BookOpen, User, ArrowRight, X,
  Cloud, Link2, ExternalLink, ChevronDown, ChevronUp,
  Download, Zap, Settings2, GitPullRequest
} from 'lucide-react';

const stateColors = {
  TODO: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  IN_REVIEW: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  DONE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  // ADO states
  New: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  Active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Resolved: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Closed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const typeIcons = {
  'Epic': Package,
  'Feature': Layers,
  'User Story': BookOpen,
};

export default function ADOSettings() {
  const can = usePermissions();
  const canEdit = can('editWork');
  const canConfigure = can('configureAdo');
  const canPull = can('pullAdo');
  const [activeTab, setActiveTab] = useState('connection'); // connection, csv, live
  const [config, setConfig] = useState(null);
  const [configForm, setConfigForm] = useState({ organization: '', project: '', auto_create_enabled: false });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);
  
  // CSV Import state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Live sync state
  const [areas, setAreas] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedIteration, setSelectedIteration] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [liveItems, setLiveItems] = useState([]);
  const [loadingLive, setLoadingLive] = useState(false);
  const [selectedADOIds, setSelectedADOIds] = useState([]);
  const [syncingLive, setSyncingLive] = useState(false);
  const [liveResult, setLiveResult] = useState(null);
  
  const [importStatus, setImportStatus] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  
  // Team sync state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [syncingTeam, setSyncingTeam] = useState(false);
  const [teamSyncResult, setTeamSyncResult] = useState(null);
  
  // Assignee linking state
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const [assigneeLinks, setAssigneeLinks] = useState([]);
  const [unmatchedAssignees, setUnmatchedAssignees] = useState([]);
  const [linkingSummary, setLinkingSummary] = useState(null);
  const [autoLinking, setAutoLinking] = useState(false);

  // Repository & PR sync state
  const [repositories, setRepositories] = useState([]);
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [syncingPRs, setSyncingPRs] = useState(false);
  const [prSyncResult, setPrSyncResult] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadImportStatus();
  }, []);

  useEffect(() => {
    if (config?.connected && activeTab === 'live') {
      loadAreasAndIterations();
    }
  }, [config, activeTab]);

  const loadConfig = async () => {
    try {
      const res = await api.get('/devops/config');
      setConfig(res.data);
      if (res.data.organization) {
        setConfigForm({
          organization: res.data.organization,
          project: res.data.project,
          auto_create_enabled: res.data.auto_create_enabled || false
        });
      }
    } catch (err) { console.error(err); }
  };

  const loadImportStatus = async () => {
    try {
      const res = await api.get('/devops/import/status');
      setImportStatus(res.data);
    } catch (err) { console.error(err); }
  };

  const loadAreasAndIterations = async () => {
    try {
      const [areasRes, iterRes] = await Promise.all([
        api.get('/devops/pull/areas'),
        api.get('/devops/pull/iterations')
      ]);
      setAreas(areasRes.data.areas || []);
      setIterations(iterRes.data.iterations || []);
    } catch (err) { 
      console.error(err);
    }
  };

  // Load locally synced team members
  const loadLocalTeam = async () => {
    setLoadingTeam(true);
    try {
      const res = await api.get('/devops/team/local');
      setTeamMembers(res.data.members || []);
    } catch (err) {
      console.error('Failed to load team members:', err);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Sync team members from ADO
  const syncTeamFromADO = async () => {
    setSyncingTeam(true);
    setTeamSyncResult(null);
    try {
      const res = await api.post('/devops/team/sync');
      setTeamSyncResult({ success: true, synced: res.data.synced, total: res.data.total });
      await loadLocalTeam(); // Reload the list
    } catch (err) {
      console.error('Failed to sync team:', err);
      setTeamSyncResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setSyncingTeam(false);
    }
  };

  // Auto-link assignees to ADO team members
  const runAutoLink = async () => {
    setAutoLinking(true);
    try {
      const res = await api.post('/devops/team/auto-link');
      setAssigneeLinks(res.data.links || []);
      setUnmatchedAssignees(res.data.unmatched || []);
      setLinkingSummary(res.data.summary);
      setShowLinkingModal(true);
    } catch (err) {
      console.error('Auto-link failed:', err);
      alert(err.response?.data?.detail || 'Failed to auto-link assignees');
    } finally {
      setAutoLinking(false);
    }
  };

  // Load existing links
  const loadExistingLinks = async () => {
    try {
      const res = await api.get('/devops/team/links');
      setAssigneeLinks(res.data.links || []);
    } catch (err) {
      console.error('Failed to load links:', err);
    }
  };

  // Delete a link
  const deleteLink = async (assignee) => {
    try {
      await api.delete(`/devops/team/link/${encodeURIComponent(assignee)}`);
      setAssigneeLinks(prev => prev.filter(l => l.assignee !== assignee));
    } catch (err) {
      console.error('Failed to delete link:', err);
    }
  };

  // Load team when switching to team tab
  useEffect(() => {
    if (config?.connected && activeTab === 'team') {
      loadLocalTeam();
      loadExistingLinks();
    }
  }, [config, activeTab]);

  // Load repos when switching to repos tab
  useEffect(() => {
    if (config?.connected && activeTab === 'repos') {
      loadRepositories();
    }
  }, [config, activeTab]);

  const loadRepositories = async () => {
    setLoadingRepos(true);
    try {
      const res = await api.get('/devops/repositories');
      setRepositories(res.data.repositories || []);
      // Pre-select repos from saved config
      if (config?.repositories) {
        setSelectedRepos(config.repositories.map(r => r.id));
      }
    } catch (err) {
      console.error('Failed to load repositories:', err);
    } finally {
      setLoadingRepos(false);
    }
  };

  const saveRepoSelection = async () => {
    try {
      const selected = repositories.filter(r => selectedRepos.includes(r.id));
      await api.put('/devops/repositories', { repositories: selected });
      await loadConfig();
      setPrSyncResult({ success: true, message: `${selected.length} repositories saved` });
    } catch (err) {
      console.error('Failed to save repos:', err);
      setPrSyncResult({ success: false, error: err.response?.data?.detail || err.message });
    }
  };

  const syncPullRequests = async () => {
    setSyncingPRs(true);
    setPrSyncResult(null);
    try {
      const res = await api.post('/devops/pull-requests/sync');
      setPrSyncResult({ success: true, message: `${res.data.synced} pull requests synced`, errors: res.data.errors });
    } catch (err) {
      console.error('Failed to sync PRs:', err);
      setPrSyncResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setSyncingPRs(false);
    }
  };

  const toggleRepoSelection = (repoId) => {
    setSelectedRepos(prev =>
      prev.includes(repoId) ? prev.filter(id => id !== repoId) : [...prev, repoId]
    );
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    setConfigError(null);
    try {
      const res = await api.post('/devops/config', configForm);
      await loadConfig();
      setActiveTab('live'); // Switch to live tab after successful connection
    } catch (err) { 
      console.error(err);
      setConfigError(err.response?.data?.detail || 'Connection failed');
    }
    finally { setSavingConfig(false); }
  };

  const pullLiveItems = async () => {
    setLoadingLive(true);
    setLiveItems([]);
    setSelectedADOIds([]);
    setLiveResult(null);
    try {
      const params = new URLSearchParams();
      if (selectedArea) params.append('area_path', selectedArea);
      if (selectedIteration) params.append('iteration_path', selectedIteration);
      if (selectedType) params.append('work_item_type', selectedType);
      
      const res = await api.post(`/devops/pull/workitems?${params.toString()}`);
      setLiveItems(res.data.items || []);
    } catch (err) {
      console.error(err);
      alert('Failed to fetch work items: ' + (err.response?.data?.detail || err.message));
    }
    finally { setLoadingLive(false); }
  };

  const syncSelectedItems = async () => {
    if (selectedADOIds.length === 0) return;
    setSyncingLive(true);
    try {
      const res = await api.post('/devops/pull/sync', selectedADOIds);
      setLiveResult(res.data);
      await loadImportStatus();
    } catch (err) {
      console.error(err);
      setLiveResult({ success: false, errors: [err.response?.data?.detail || err.message] });
    }
    finally { setSyncingLive(false); }
  };

  const toggleSelectAll = () => {
    if (selectedADOIds.length === liveItems.length) {
      setSelectedADOIds([]);
    } else {
      setSelectedADOIds(liveItems.map(i => i.ado_id));
    }
  };

  const toggleSelectItem = (adoId) => {
    if (selectedADOIds.includes(adoId)) {
      setSelectedADOIds(selectedADOIds.filter(id => id !== adoId));
    } else {
      setSelectedADOIds([...selectedADOIds, adoId]);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setPreview(null);
      setImportResult(null);
    }
  };

  const previewImport = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/devops/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
    } catch (err) { 
      console.error(err);
      alert('Failed to preview CSV. Please check the file format.');
    }
    finally { setPreviewing(false); }
  };

  const executeImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/devops/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      await loadImportStatus();
    } catch (err) { 
      console.error(err);
      setImportResult({ success: false, errors: ['Import failed. Please try again.'] });
    }
    finally { setImporting(false); }
  };

  const toggleExpanded = (ado_id) => {
    setExpandedItems(prev => ({ ...prev, [ado_id]: !prev[ado_id] }));
  };

  const TypeIcon = ({ type }) => {
    const Icon = typeIcons[type] || BookOpen;
    return <Icon size={14} />;
  };

  return (
    <div className="p-4 lg:p-8" data-testid="ado-settings-page">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Settings</p>
        <h1 className="text-3xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.6px' }}>
          Azure DevOps Integration
        </h1>
        <p className="text-[#30302F] dark:text-white mt-2 font-light">
          Connect to Azure DevOps for bi-directional sync of work items.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-[#e3e3e3] dark:border-white/10">
        <button
          onClick={() => setActiveTab('connection')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'connection' 
              ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px' 
              : 'text-[#30302F] dark:text-white hover:text-[#98139C]'
          }`}
        >
          <Settings2 size={16} /> Connection
        </button>
        <button
          onClick={() => setActiveTab('live')}
          disabled={!config?.connected}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'live' 
              ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px' 
              : 'text-[#30302F] dark:text-white hover:text-[#98139C]'
          } ${!config?.connected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Zap size={16} /> Live Sync
        </button>
        <button
          onClick={() => setActiveTab('team')}
          disabled={!config?.connected}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'team' 
              ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px' 
              : 'text-[#30302F] dark:text-white hover:text-[#98139C]'
          } ${!config?.connected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <User size={16} /> Team Members
        </button>
        <button
          onClick={() => setActiveTab('repos')}
          disabled={!config?.connected}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'repos'
              ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px'
              : 'text-[#30302F] dark:text-white hover:text-[#98139C]'
          } ${!config?.connected ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <GitPullRequest size={16} /> Repositories
        </button>
        <button
          onClick={() => setActiveTab('csv')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'csv' 
              ? 'text-[#98139C] border-b-2 border-[#98139C] -mb-px' 
              : 'text-[#30302F] dark:text-white hover:text-[#98139C]'
          }`}
        >
          <Upload size={16} /> CSV Import
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Connection Tab */}
          {activeTab === 'connection' && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cloud size={20} className="text-[#0078d4]" />
                <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Connection Settings</h2>
              </div>
              {config?.connected && (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-none text-[11px]">
                  <CheckCircle size={10} className="mr-1" /> Connected
                </Badge>
              )}
            </div>
            <div className="p-5 space-y-4">
              {!canConfigure && (
                <p className="text-xs text-[#30302F]/70 dark:text-white/70">Read only. Admins change the Azure DevOps connection.</p>
              )}
              <fieldset disabled={!canConfigure} className="contents">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1.5 block">
                    Organization
                  </label>
                  <Input
                    value={configForm.organization}
                    onChange={(e) => setConfigForm({ ...configForm, organization: e.target.value })}
                    placeholder="your-org"
                    className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white"
                    data-testid="ado-org-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1.5 block">
                    Project
                  </label>
                  <Input
                    value={configForm.project}
                    onChange={(e) => setConfigForm({ ...configForm, project: e.target.value })}
                    placeholder="your-project"
                    className="rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white"
                    data-testid="ado-project-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-[#30302F] dark:text-white">
                The Personal Access Token is held in the server configuration (ADO_PAT_TOKEN) and is never entered or stored here.
              </p>
              
              {/* Auto-create toggle */}
              <div className="flex items-center justify-between py-3 border-t border-[#e3e3e3] dark:border-white/10">
                <div>
                  <p className="text-sm font-medium text-[#30302F] dark:text-white">Auto-create Work Items</p>
                  <p className="text-[11px] text-[#30302F]/80 dark:text-white/80">
                    Automatically create work items in ADO when stories are created in SPEEDFORCE
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfigForm({ ...configForm, auto_create_enabled: !configForm.auto_create_enabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    configForm.auto_create_enabled ? 'bg-[#98139C]' : 'bg-gray-300 dark:bg-white/20'
                  }`}
                  data-testid="auto-create-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      configForm.auto_create_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <button
                onClick={saveConfig}
                disabled={savingConfig || !configForm.organization || !configForm.project}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
                data-testid="save-config-btn"
              >
                {savingConfig ? (
                  <><RefreshCw size={14} className="animate-spin" /> Testing Connection...</>
                ) : (
                  'Connect to Azure DevOps'
                )}
              </button>
              </fieldset>
              {configError && (
                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle size={14} className="inline mr-2" />
                  {configError}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Live Sync Tab */}
          {activeTab === 'live' && config?.connected && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10">
              <div className="flex items-center gap-3">
                <Zap size={20} className="text-[#0078d4]" />
                <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Pull from Azure DevOps</h2>
              </div>
              <p className="text-sm text-[#30302F] dark:text-white mt-1 font-light">
                Fetch work items directly from ADO and import them into Speedforce.
              </p>
            </div>
            <div className="p-5 space-y-4">
              {/* Filters */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1.5 block">
                    Area Path
                  </label>
                  <select
                    value={selectedArea}
                    onChange={(e) => setSelectedArea(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white"
                  >
                    <option value="">All Areas</option>
                    {areas.map(a => (
                      <option key={a.path} value={a.path}>{a.path}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1.5 block">
                    Iteration
                  </label>
                  <select
                    value={selectedIteration}
                    onChange={(e) => setSelectedIteration(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white"
                  >
                    <option value="">All Iterations</option>
                    {iterations.map(i => (
                      <option key={i.path} value={i.path}>{i.path}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white mb-1.5 block">
                    Work Item Type
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full h-9 px-3 text-sm border border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white"
                  >
                    <option value="">All Types</option>
                    <option value="Epic">Epic</option>
                    <option value="Feature">Feature</option>
                    <option value="User Story">User Story</option>
                    <option value="Task">Task</option>
                    <option value="Bug">Bug</option>
                  </select>
                </div>
              </div>
              
              <button
                onClick={pullLiveItems}
                disabled={!canPull || loadingLive}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {loadingLive ? (
                  <><RefreshCw size={14} className="animate-spin" /> Fetching...</>
                ) : (
                  <><Download size={14} /> Fetch Work Items</>
                )}
              </button>

              {/* Live Items List */}
              {liveItems.length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#30302F] dark:text-white">
                      Found <strong>{liveItems.length}</strong> work items
                    </p>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedADOIds.length === liveItems.length}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                      Select All
                    </label>
                  </div>
                  
                  <div className="max-h-[400px] overflow-y-auto border border-[#e3e3e3] dark:border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-[#faf8fb] dark:bg-white/5 sticky top-0">
                        <tr>
                          <th className="w-10 p-3"></th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">ID</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Type</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Title</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Sprint</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">State</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveItems.map((item, idx) => (
                          <tr 
                            key={item.ado_id}
                            className={`border-t border-[#e3e3e3] dark:border-white/10 hover:bg-[#f9f5fa] dark:hover:bg-white/5 ${
                              idx % 2 === 0 ? '' : 'bg-[#faf8fb] dark:bg-white/[0.02]'
                            }`}
                          >
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedADOIds.includes(item.ado_id)}
                                onChange={() => toggleSelectItem(item.ado_id)}
                                className="rounded"
                              />
                            </td>
                            <td className="p-3">
                              <a 
                                href={item.ado_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-mono text-xs text-[#0078d4] hover:underline"
                              >
                                #{item.ado_id}
                              </a>
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 ${
                                item.work_item_type === 'Epic' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                item.work_item_type === 'Feature' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                item.work_item_type === 'Bug' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                item.work_item_type === 'Task' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                              }`}>
                                <TypeIcon type={item.work_item_type} />
                                {item.work_item_type}
                              </span>
                            </td>
                            <td className="p-3 text-[#30302F] dark:text-white max-w-[250px] truncate">
                              {item.title}
                            </td>
                            <td className="p-3 text-xs max-w-[120px] truncate" title={item.iteration_path || 'Backlog'}>
                              {item.iteration_path ? (
                                <span className="text-[#30302F] dark:text-white">
                                  {item.iteration_path.split('\\').pop()}
                                </span>
                              ) : (
                                <span className="text-amber-600 dark:text-amber-400 font-medium">Backlog</span>
                              )}
                            </td>
                            <td className="p-3">
                              <Badge className={`rounded-none text-[11px] ${stateColors[item.state] || stateColors.New}`}>
                                {item.state}
                              </Badge>
                            </td>
                            <td className="p-3 text-[#30302F] dark:text-white">
                              {item.story_points || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Import Selected */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                    <p className="text-sm text-[#30302F] dark:text-white">
                      <strong>{selectedADOIds.length}</strong> items selected for import
                    </p>
                    <button
                      onClick={syncSelectedItems}
                      disabled={!canEdit || syncingLive || selectedADOIds.length === 0}
                      className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      {syncingLive ? (
                        <><RefreshCw size={14} className="animate-spin" /> Importing...</>
                      ) : (
                        <><ArrowRight size={14} /> Import to Speedforce</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Live Import Result */}
              {liveResult && (
                <div className={`mt-4 p-4 border ${
                  liveResult.success 
                    ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700/30' 
                    : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700/30'
                }`}>
                  <div className="flex items-start gap-3">
                    {liveResult.success ? (
                      <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                    )}
                    <div>
                      <h4 className={`font-bold ${liveResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                        {liveResult.success ? 'Import Successful!' : 'Import Completed with Errors'}
                      </h4>
                      <div className="mt-2 text-sm space-y-1">
                        <p className="text-[#30302F] dark:text-white">
                          • Products: <strong>{liveResult.products_created || 0}</strong> | 
                          Intakes: <strong>{liveResult.intakes_created || 0}</strong> | 
                          Stories: <strong>{liveResult.stories_created || 0}</strong>
                          {liveResult.stories_updated > 0 && <> (updated: {liveResult.stories_updated})</>}
                        </p>
                        {liveResult.sprints_created > 0 && (
                          <p className="text-[#30302F] dark:text-white">
                            • Sprints created: <strong>{liveResult.sprints_created}</strong>
                          </p>
                        )}
                        {liveResult.backlog_items > 0 && (
                          <p className="text-amber-600 dark:text-amber-400">
                            • Backlog items (no sprint): <strong>{liveResult.backlog_items}</strong>
                          </p>
                        )}
                      </div>
                      {liveResult.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                          {liveResult.errors.map((err, i) => <p key={i}>{err}</p>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* CSV Import Tab */}
          {activeTab === 'csv' && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10">
              <div className="flex items-center gap-3">
                <Upload size={20} className="text-[#98139C]" />
                <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Import from CSV</h2>
              </div>
              <p className="text-sm text-[#30302F] dark:text-white mt-1 font-light">
                Upload an exported CSV from Azure DevOps to import work items.
              </p>
            </div>
            <div className="p-5">
              {/* File Upload */}
              <div 
                className={`border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
                  file 
                    ? 'border-[#98139C] bg-[#f9f5fa] dark:bg-[#98139C]/10' 
                    : 'border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C]'
                }`}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText size={24} className="text-[#98139C]" />
                    <div className="text-left">
                      <p className="font-medium text-[#30302F] dark:text-white">{file.name}</p>
                      <p className="text-xs text-[#30302F] dark:text-white">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                      className="ml-4 p-1 hover:bg-[#98139C]/10 rounded"
                    >
                      <X size={16} className="text-[#30302F]" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="mx-auto mb-3 text-[#30302F] dark:text-white" />
                    <p className="text-[#30302F] dark:text-white">
                      Click to select or drag & drop your CSV file
                    </p>
                    <p className="text-xs text-[#30302F] dark:text-white mt-1">
                      Supports Azure DevOps work item exports
                    </p>
                  </>
                )}
              </div>

              {/* Actions */}
              {file && !preview && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={previewImport}
                    disabled={!canEdit || previewing}
                    className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="preview-import-btn"
                  >
                    {previewing ? (
                      <><RefreshCw size={14} className="animate-spin" /> Analyzing...</>
                    ) : (
                      <>Preview Import</>
                    )}
                  </button>
                </div>
              )}

              {/* Preview Results */}
              {preview && (
                <div className="mt-6 space-y-4" data-testid="import-preview">
                  {/* Summary */}
                  <div className="bg-[#f9f5fa] dark:bg-[#98139C]/10 p-4 border border-[#98139C]/20">
                    <h3 className="font-bold text-[#4F0077] dark:text-[#d87bdb] mb-3">Import Summary</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-heading text-[#98139C]">{preview.total_items}</p>
                        <p className="text-[11px] uppercase tracking-wide text-[#30302F] dark:text-white">Total Items</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading text-[#30302F] dark:text-white">{preview.epics}</p>
                        <p className="text-[11px] uppercase tracking-wide text-[#30302F] dark:text-white">Epics</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading text-[#30302F] dark:text-white">{preview.features}</p>
                        <p className="text-[11px] uppercase tracking-wide text-[#30302F] dark:text-white">Features</p>
                      </div>
                      <div>
                        <p className="text-2xl font-heading text-[#30302F] dark:text-white">{preview.user_stories}</p>
                        <p className="text-[11px] uppercase tracking-wide text-[#30302F] dark:text-white">User Stories</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#98139C]/20">
                      <p className="text-xs text-[#30302F] dark:text-white">
                        <strong>Products:</strong> {preview.products.join(', ')}
                      </p>
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="max-h-[400px] overflow-y-auto border border-[#e3e3e3] dark:border-white/10">
                    <table className="w-full text-sm">
                      <thead className="bg-[#faf8fb] dark:bg-white/5 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">ID</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Type</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Title</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">Product</th>
                          <th className="text-left p-3 text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.items.map((item, idx) => (
                          <>
                            <tr 
                              key={item.ado_id} 
                              className={`border-t border-[#e3e3e3] dark:border-white/10 hover:bg-[#f9f5fa] dark:hover:bg-white/5 cursor-pointer ${
                                idx % 2 === 0 ? '' : 'bg-[#faf8fb] dark:bg-white/[0.02]'
                              }`}
                              onClick={() => toggleExpanded(item.ado_id)}
                            >
                              <td className="p-3 font-mono text-xs text-[#30302F] dark:text-white">{item.ado_id}</td>
                              <td className="p-3">
                                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 ${
                                  item.item_type === 'Epic' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                  item.item_type === 'Feature' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                                }`}>
                                  <TypeIcon type={item.item_type} />
                                  {item.item_type}
                                </span>
                              </td>
                              <td className="p-3 text-[#30302F] dark:text-white max-w-[300px]">
                                <div className="flex items-center gap-2">
                                  {expandedItems[item.ado_id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  <span className="truncate">{item.title}</span>
                                </div>
                              </td>
                              <td className="p-3 text-[#30302F] dark:text-white">{item.product}</td>
                              <td className="p-3">
                                <Badge className={`rounded-none text-[11px] ${stateColors[item.state] || stateColors.TODO}`}>
                                  {item.state}
                                </Badge>
                              </td>
                            </tr>
                            {expandedItems[item.ado_id] && (
                              <tr className="bg-[#f9f5fa] dark:bg-white/5">
                                <td colSpan={5} className="p-4">
                                  <div className="space-y-3">
                                    <div>
                                      <p className="text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white mb-1">Full Title</p>
                                      <p className="text-sm text-[#30302F] dark:text-white">{item.full_title}</p>
                                    </div>
                                    {item.acceptance_criteria?.length > 0 && (
                                      <div>
                                        <p className="text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white mb-1">Acceptance Criteria</p>
                                        <ul className="space-y-1">
                                          {item.acceptance_criteria.map((ac, i) => (
                                            <li key={i} className="text-xs text-[#30302F] dark:text-white flex items-start gap-2">
                                              <CheckCircle size={12} className="text-green-500 mt-0.5 shrink-0" />
                                              {ac}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-4 text-xs">
                                      <span className="text-[#30302F] dark:text-white">
                                        <strong>Action:</strong> {item.action}
                                      </span>
                                      <span className="text-[#30302F] dark:text-white">
                                        <strong>Maps to:</strong> {item.pilot_mapping}
                                      </span>
                                      {item.assigned_to && (
                                        <span className="text-[#30302F] dark:text-white flex items-center gap-1">
                                          <User size={12} /> {item.assigned_to}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Import Button */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                    <p className="text-sm text-[#30302F] dark:text-white">
                      This will create <strong>{preview.products.length}</strong> products, and import <strong>{preview.total_items}</strong> work items.
                    </p>
                    <button
                      onClick={executeImport}
                      disabled={!canEdit || importing}
                      className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-6 h-10 flex items-center gap-2 transition-colors disabled:opacity-50"
                      data-testid="execute-import-btn"
                    >
                      {importing ? (
                        <><RefreshCw size={14} className="animate-spin" /> Importing...</>
                      ) : (
                        <><ArrowRight size={14} /> Import All Items</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <div className={`mt-6 p-4 border ${
                  importResult.success 
                    ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700/30' 
                    : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700/30'
                }`} data-testid="import-result">
                  <div className="flex items-start gap-3">
                    {importResult.success ? (
                      <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`font-bold ${importResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                        {importResult.success ? 'Import Successful!' : 'Import Completed with Errors'}
                      </h4>
                      <div className="mt-2 text-sm space-y-1">
                        <p className="text-[#30302F] dark:text-white">
                          • Products created: <strong>{importResult.products_created}</strong>
                        </p>
                        <p className="text-[#30302F] dark:text-white">
                          • Intake items created: <strong>{importResult.intakes_created}</strong>
                        </p>
                        <p className="text-[#30302F] dark:text-white">
                          • User stories created: <strong>{importResult.stories_created}</strong>
                        </p>
                        <p className="text-[#30302F] dark:text-white">
                          • User stories updated: <strong>{importResult.stories_updated}</strong>
                        </p>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-700/30">
                          <p className="text-xs font-bold text-red-700 dark:text-red-400 mb-1">Errors:</p>
                          {importResult.errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-600 dark:text-red-400">{err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Team Members Tab */}
          {activeTab === 'team' && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User size={20} className="text-[#98139C]" />
                <div>
                  <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Team Members</h2>
                  <p className="text-sm text-[#30302F] dark:text-white font-light">
                    Sync team members from Azure DevOps for assignment linking.
                  </p>
                </div>
              </div>
              <button
                onClick={syncTeamFromADO}
                disabled={!canEdit || syncingTeam}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
                data-testid="sync-team-btn"
              >
                {syncingTeam ? (
                  <><RefreshCw size={14} className="animate-spin" /> Syncing...</>
                ) : (
                  <><Download size={14} /> Sync from ADO</>
                )}
              </button>
            </div>
            
            <div className="p-5">
              {/* Sync Result */}
              {teamSyncResult && (
                <div className={`mb-4 p-4 border ${
                  teamSyncResult.success 
                    ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700/30' 
                    : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-700/30'
                }`}>
                  {teamSyncResult.success ? (
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                      <CheckCircle size={16} />
                      <span className="text-sm font-medium">
                        Synced {teamSyncResult.synced} team members from ADO
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                      <AlertCircle size={16} />
                      <span className="text-sm font-medium">{teamSyncResult.error}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Team Members List */}
              {loadingTeam ? (
                <div className="text-center py-10">
                  <RefreshCw size={24} className="animate-spin mx-auto text-[#98139C] mb-2" />
                  <p className="text-sm text-[#30302F] dark:text-white">Loading team members...</p>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-[#e3e3e3] dark:border-white/10">
                  <User size={40} className="mx-auto mb-3 text-[#30302F]/30 dark:text-white/30" />
                  <p className="text-[#30302F] dark:text-white mb-2">No team members synced yet</p>
                  <p className="text-sm text-[#30302F]/70 dark:text-white/70">
                    Click "Sync from ADO" to import team members from Azure DevOps
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-[#30302F] dark:text-white">
                      <strong>{teamMembers.length}</strong> team members synced
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={runAutoLink}
                        disabled={!canEdit || autoLinking}
                        className="text-xs bg-[#98139C] text-white hover:bg-[#7a0f7d] px-3 py-1.5 rounded-full font-medium flex items-center gap-1 disabled:opacity-50"
                        data-testid="auto-link-btn"
                      >
                        {autoLinking ? (
                          <><RefreshCw size={12} className="animate-spin" /> Linking...</>
                        ) : (
                          <><Link2 size={12} /> Auto-Link Assignees</>
                        )}
                      </button>
                      <button
                        onClick={loadLocalTeam}
                        className="text-xs text-[#98139C] hover:underline font-medium flex items-center gap-1"
                      >
                        <RefreshCw size={12} /> Refresh
                      </button>
                    </div>
                  </div>

                  {/* Existing Links Summary */}
                  {assigneeLinks.length > 0 && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-700/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                          <CheckCircle size={16} />
                          <span className="text-sm font-medium">
                            {assigneeLinks.length} assignees linked to ADO team members
                          </span>
                        </div>
                        <button
                          onClick={() => setShowLinkingModal(true)}
                          className="text-xs text-green-700 dark:text-green-400 hover:underline font-medium"
                        >
                          View Details →
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {teamMembers.map((member) => (
                      <div 
                        key={member.ado_id}
                        className="flex items-center gap-3 p-3 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors"
                      >
                        {member.image_url ? (
                          <img 
                            src={member.image_url} 
                            alt={member.display_name}
                            className="w-10 h-10 rounded-full bg-[#f0dcf0]"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center">
                            <User size={16} className="text-[#98139C]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#30302F] dark:text-white truncate">
                            {member.display_name}
                          </p>
                          <p className="text-xs text-[#30302F]/70 dark:text-white/70 truncate">
                            {member.email || member.unique_name}
                          </p>
                        </div>
                        {member.is_team_admin && (
                          <Badge className="bg-[#98139C]/10 text-[#98139C] text-[10px] rounded-none">
                            Admin
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Repositories Tab */}
          {activeTab === 'repos' && (
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GitPullRequest size={20} className="text-[#98139C]" />
                <div>
                  <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Repositories</h2>
                  <p className="text-sm text-[#30302F] dark:text-white font-light">
                    Select repositories to sync pull requests for flow metrics.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5">
              {loadingRepos ? (
                <div className="flex items-center justify-center py-8" role="status" aria-label="Loading repositories">
                  <div className="w-6 h-6 border-2 border-[#98139C]/30 border-t-[#98139C] rounded-full animate-spin" aria-hidden="true" />
                  <span className="sr-only">Loading repositories…</span>
                </div>
              ) : repositories.length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {repositories.map((repo) => (
                      <label
                        key={repo.id}
                        className="flex items-center gap-3 p-3 border border-[#e3e3e3] dark:border-white/10 hover:border-[#98139C] transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRepos.includes(repo.id)}
                          onChange={() => toggleRepoSelection(repo.id)}
                          className="accent-[#98139C] w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#30302F] dark:text-white truncate">{repo.name}</p>
                          <p className="text-xs text-[#30302F]/60 dark:text-white/60 truncate">{repo.default_branch}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 pt-2 border-t border-[#e3e3e3] dark:border-white/10">
                    <button
                      onClick={saveRepoSelection}
                      disabled={!canEdit || selectedRepos.length === 0}
                      className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle size={14} /> Save Selection ({selectedRepos.length})
                    </button>
                    <button
                      onClick={syncPullRequests}
                      disabled={!canEdit || syncingPRs || !config?.repositories?.length}
                      className="border border-[#98139C] text-[#98139C] hover:bg-[#98139C]/5 rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                      {syncingPRs ? (
                        <><RefreshCw size={14} className="animate-spin" /> Syncing PRs...</>
                      ) : (
                        <><GitPullRequest size={14} /> Sync Pull Requests</>
                      )}
                    </button>
                  </div>

                  {prSyncResult && (
                    <div className={`p-3 text-sm ${
                      prSyncResult.success
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 text-green-700 dark:text-green-400'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-400'
                    }`}>
                      {prSyncResult.success ? (
                        <><CheckCircle size={14} className="inline mr-2" />{prSyncResult.message}</>
                      ) : (
                        <><AlertCircle size={14} className="inline mr-2" />{prSyncResult.error}</>
                      )}
                      {prSyncResult.errors?.length > 0 && (
                        <ul className="mt-2 text-xs space-y-1">
                          {prSyncResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-[#30302F]/60 dark:text-white/60">
                  <GitPullRequest size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No repositories found in this project.</p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Right Column - Status */}
        <div className="space-y-6">
          {/* Import Status */}
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10">
              <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Import Status</h2>
            </div>
            <div className="p-5">
              {importStatus ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-[#f9f5fa] dark:bg-[#98139C]/10 p-3">
                      <p className="text-xl font-heading text-[#98139C]">{importStatus.total_products}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#30302F] dark:text-white">Products</p>
                    </div>
                    <div className="bg-[#f9f5fa] dark:bg-[#98139C]/10 p-3">
                      <p className="text-xl font-heading text-[#98139C]">{importStatus.total_intakes}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#30302F] dark:text-white">Intakes</p>
                    </div>
                    <div className="bg-[#f9f5fa] dark:bg-[#98139C]/10 p-3">
                      <p className="text-xl font-heading text-[#98139C]">{importStatus.total_stories}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#30302F] dark:text-white">Stories</p>
                    </div>
                  </div>

                  {importStatus.stories_by_state && Object.keys(importStatus.stories_by_state).length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white mb-2">By State</p>
                      <div className="space-y-2">
                        {Object.entries(importStatus.stories_by_state).map(([state, count]) => (
                          <div key={state} className="flex items-center justify-between text-sm">
                            <Badge className={`rounded-none text-[11px] ${stateColors[state] || stateColors.TODO}`}>
                              {state}
                            </Badge>
                            <span className="text-[#30302F] dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {importStatus.stories_by_product && Object.keys(importStatus.stories_by_product).length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wide font-bold text-[#30302F] dark:text-white mb-2">By Product</p>
                      <div className="space-y-2">
                        {Object.entries(importStatus.stories_by_product).map(([product, count]) => (
                          <div key={product} className="flex items-center justify-between text-sm">
                            <span className="text-[#30302F] dark:text-white flex items-center gap-1.5">
                              <Package size={12} className="text-[#98139C]" />
                              {product}
                            </span>
                            <span className="text-[#30302F] dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={loadImportStatus}
                    className="w-full text-xs text-[#98139C] hover:underline font-medium flex items-center justify-center gap-1 mt-2"
                  >
                    <RefreshCw size={12} /> Refresh Status
                  </button>
                </div>
              ) : (
                <div className="text-center py-6 text-[#30302F] dark:text-white">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No imports yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10">
              <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Quick Links</h2>
            </div>
            <div className="p-5 space-y-3">
              <a
                href="https://dev.azure.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border border-[#e3e3e3] dark:border-white/10 hover:border-[#0078d4] transition-colors group"
              >
                <span className="text-sm text-[#30302F] dark:text-white flex items-center gap-2">
                  <Cloud size={16} className="text-[#0078d4]" />
                  Azure DevOps Portal
                </span>
                <ExternalLink size={14} className="text-[#30302F] group-hover:text-[#0078d4]" />
              </a>
              <a
                href="https://learn.microsoft.com/en-us/azure/devops/boards/queries/query-by-workflow-changes"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border border-[#e3e3e3] dark:border-white/10 hover:border-[#0078d4] transition-colors group"
              >
                <span className="text-sm text-[#30302F] dark:text-white flex items-center gap-2">
                  <FileText size={16} className="text-[#0078d4]" />
                  Export Work Items Guide
                </span>
                <ExternalLink size={14} className="text-[#30302F] group-hover:text-[#0078d4]" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Assignee Linking Modal */}
      {showLinkingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link2 size={20} className="text-[#98139C]" />
                <div>
                  <h2 className="text-lg font-heading text-[#30302F] dark:text-white">Assignee Links</h2>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">
                    {linkingSummary ? `${linkingSummary.matched}/${linkingSummary.total_assignees} linked (${linkingSummary.match_rate}%)` : `${assigneeLinks.length} total links`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLinkingModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded"
              >
                <X size={20} className="text-[#30302F] dark:text-white" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-5">
              {/* Linked Assignees */}
              {assigneeLinks.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-[#30302F] dark:text-white mb-3 flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-500" />
                    Linked Assignees ({assigneeLinks.length})
                  </h3>
                  <div className="space-y-2">
                    {assigneeLinks.map((link) => (
                      <div 
                        key={link.assignee}
                        className="flex items-center justify-between p-3 border border-[#e3e3e3] dark:border-white/10 bg-green-50/50 dark:bg-green-900/10"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#30302F] dark:text-white">
                              {link.assignee}
                            </span>
                            <ArrowRight size={14} className="text-[#30302F]/60 dark:text-white/60" />
                          </div>
                          <div className="flex items-center gap-2">
                            {link.ado_member?.image_url ? (
                              <img 
                                src={link.ado_member.image_url} 
                                alt={link.ado_member.display_name}
                                className="w-6 h-6 rounded-full"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-[#f0dcf0] dark:bg-[#98139C]/20 flex items-center justify-center">
                                <User size={12} className="text-[#98139C]" />
                              </div>
                            )}
                            <div>
                              <span className="text-sm text-[#30302F] dark:text-white">
                                {link.ado_member?.display_name}
                              </span>
                              <Badge className="ml-2 text-[10px] rounded-none bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/70">
                                {link.match_type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        {canEdit && <button
                          onClick={() => deleteLink(link.assignee)}
                          className="text-xs text-red-500 hover:text-red-700 font-medium"
                        >
                          Remove
                        </button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unmatched Assignees */}
              {unmatchedAssignees.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-[#30302F] dark:text-white mb-3 flex items-center gap-2">
                    <AlertCircle size={14} className="text-amber-500" />
                    Unmatched Assignees ({unmatchedAssignees.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {unmatchedAssignees.map((assignee) => (
                      <div 
                        key={assignee}
                        className="p-2 border border-amber-200 dark:border-amber-700/30 bg-amber-50/50 dark:bg-amber-900/10 text-sm text-[#30302F] dark:text-white"
                      >
                        {assignee}
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-[#30302F]/80 dark:text-white/80">
                    These assignees could not be matched to any ADO team member. You may need to manually assign them or update team member names in ADO.
                  </p>
                </div>
              )}

              {assigneeLinks.length === 0 && unmatchedAssignees.length === 0 && (
                <div className="text-center py-10">
                  <Link2 size={40} className="mx-auto mb-3 text-[#30302F]/30 dark:text-white/30" />
                  <p className="text-[#30302F] dark:text-white mb-2">No links found</p>
                  <p className="text-sm text-[#30302F]/70 dark:text-white/70">
                    Click "Auto-Link Assignees" to automatically match story assignees to ADO team members.
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#e3e3e3] dark:border-white/10 flex justify-end gap-3">
              <button
                onClick={runAutoLink}
                disabled={!canEdit || autoLinking}
                className="bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold text-sm px-5 h-9 flex items-center gap-2 disabled:opacity-50"
              >
                {autoLinking ? (
                  <><RefreshCw size={14} className="animate-spin" /> Re-scanning...</>
                ) : (
                  <><RefreshCw size={14} /> Re-scan & Link</>
                )}
              </button>
              <button
                onClick={() => setShowLinkingModal(false)}
                className="border border-[#e3e3e3] dark:border-white/20 text-[#30302F] dark:text-white rounded-full font-bold text-sm px-5 h-9"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
